import type { TFunction } from 'i18next';

/**
 * Localized task-event-type options for the workflow alerting UI. Built from
 * the caller's translation function so org-level text_config overrides apply
 * — a module-level constant initialized with i18n.t() would freeze the
 * labels at import.
 *
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns Array of `{ value, label }` options for task-event-type selectors
 */
const getTaskEventTypeOptions = (t: TFunction) => [
  {
    value: 'assignee',
    label: t('common.assignee'),
  },
  {
    value: 'Component',
    label: t('common.component'),
  },
  {
    value: 'description',
    label: t('common.work_unit_description'),
  },
  {
    value: 'Story point estimate',
    label: `${t('common.estimation_singular')} estimate`,
  },
  {
    value: 'IssueParentAssociation',
    label: t('common.parent_task'),
  },
  {
    value: 'issuetype',
    label: t('common.type'),
  },
  {
    value: 'labels',
    label: t('common.labels_plural'),
  },
  {
    value: 'Story Points',
    label: t('common.estimation'),
  },
  {
    value: 'priority',
    label: t('common.priority'),
  },
  {
    value: 'reporter',
    label: t('common.reporter'),
  },
  {
    value: 'timeoriginalestimate',
    label: t('common.time_original_estimate'),
  },
];

const MAPPING_RELATIONSHIP_OPTIONS = [
  {
    value: 'includes',
    label: 'Inside',
  },
  {
    value: 'excludes',
    label: 'Outside',
  },
];

type WorkflowPayload = {
  name: string;
  organization: string;
  subprojects: string[];
  steps: StepPayload[];
};

type MappingPayload = {
  external_name: string;
  order_index: number;
  is_important: boolean;
};

type StepPayload = {
  name: string;
  order_index: number;
  mappings: MappingPayload[];
};

type Mapping = {
  id: string;
  external_name: string;
  order_index: number;
  is_important: boolean;
};

type Step = {
  id: string;
  name: string;
  order_index: number;
  mappings: Mapping[];
};

type Workflow = {
  id: string;
  organization: string;
  name: string;
  subprojects: string[];
  steps: Step[];
  metadata_updated_at: string;
};

export { getTaskEventTypeOptions, MAPPING_RELATIONSHIP_OPTIONS };
export type { Mapping, Step, StepPayload, Workflow, WorkflowPayload };
